"use client"

import type { ReactNode } from "react"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { Breadcrumb } from "./breadcrumb"
import { CenteredHero } from "./centered-hero"
import { SectionHeader } from "./section-header"
import { PageSectionHeader } from "./page-section-header"
import { FeatureCard } from "./feature-card"
import { ConsiderationCard } from "./consideration-card"
import { HorizontalCarousel } from "./horizontal-carousel"
import { ProductCarouselCard, type CarouselProduct } from "./product-carousel-card"
import { ToolLinkCard } from "./tool-link-card"
import { CategoryCard } from "./category-card"
import { FAQAccordion } from "./faq-accordion"
import { CTABanner } from "./cta-banner"
import { InfoCard } from "./info-card"

interface HeroStat {
  icon: ReactNode
  value: string
  label: string
}

interface HowItWorksFeature {
  icon: ReactNode
  title: string
  description: string
}

interface ConsiderationItem {
  icon: string
  title: string
  description: string
}

interface RelatedTool {
  title: string
  description: string
  href: string
}

interface ExploreLink {
  label: string
  href: string
  icon: string
}

interface FAQItem {
  question: string
  answer: string
}

interface InfoCardItem {
  text: string
}

interface SidebarInfoCard {
  theme: "blue" | "orange"
  icon: ReactNode
  title: string
  items: InfoCardItem[]
}

export interface ToolPageTemplateProps {
  // Breadcrumb + hero
  crumbLabel: string
  heroTitle: ReactNode
  heroSubtitle: string
  heroStats: HeroStat[]
  trustBadges?: string[]

  // Main content slot — caller renders the calculator / comparison / etc.
  children: ReactNode

  // Optional sidebar content alongside the main content
  sidebar?: ReactNode

  // Optional sidebar InfoCards (rendered in the sidebar if no sidebar slot)
  sidebarCards?: SidebarInfoCard[]

  // Under-the-hood section
  howItWorks?: {
    eyebrow: string
    title: string
    description?: string
    features: HowItWorksFeature[]
  }

  // Product carousel
  productCarousel?: {
    eyebrow: string
    title: string
    description?: string
    products: CarouselProduct[]
  }

  // Consideration cards section
  considerations?: {
    eyebrow: string
    title: string
    items: ConsiderationItem[]
  }

  // Related tools section
  relatedTools?: RelatedTool[]

  // FAQ
  faqs: FAQItem[]

  // Keep exploring grid
  exploreTitle?: string
  exploreLinks: ExploreLink[]

  // CTA
  cta: {
    title: string
    description: string
    buttonText: string
    buttonHref: string
  }

  // Layout hints
  /** When true, wraps main + sidebar in a 2/3 + 1/3 grid. */
  hasSidebar?: boolean
}

/**
 * ToolPageTemplate — the shared brand-kit tool page scaffold used by every
 * tool page. Renders: header → hero → main+sidebar → how it works → product
 * carousel → considerations → related tools → FAQ → keep exploring → CTA →
 * footer. Sections are optional via props.
 */
export function ToolPageTemplate({
  crumbLabel,
  heroTitle,
  heroSubtitle,
  heroStats,
  trustBadges,
  children,
  sidebar,
  sidebarCards,
  howItWorks,
  productCarousel,
  considerations,
  relatedTools,
  faqs,
  exploreTitle = "More Kitchen Tools",
  exploreLinks,
  cta,
  hasSidebar = false,
}: ToolPageTemplateProps) {
  const renderedSidebar =
    sidebar ??
    (sidebarCards && sidebarCards.length > 0 ? (
      <div className="space-y-4">
        {sidebarCards.map((c, i) => (
          <InfoCard key={i} theme={c.theme} icon={c.icon} title={c.title} items={c.items} />
        ))}
      </div>
    ) : null)

  const showSidebar = hasSidebar && renderedSidebar !== null

  return (
    <>
      <SiteHeader />

      {/* === HERO === */}
      <section className="bg-white pt-6 pb-10 md:pt-8 md:pb-12 border-b border-gray-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "Home", href: "/" },
                { label: "Tools", href: "/tools" },
                { label: crumbLabel },
              ]}
            />
          </div>
          <CenteredHero
            title={heroTitle}
            subtitle={heroSubtitle}
            stats={heroStats}
            trustBadges={trustBadges}
          />
        </div>
      </section>

      {/* === MAIN CONTENT === */}
      <section className="bg-gray-50 py-10 md:py-14">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          {showSidebar ? (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">{children}</div>
              <div>{renderedSidebar}</div>
            </div>
          ) : (
            children
          )}
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      {howItWorks && (
        <section className="bg-white pt-20 pb-14 md:pt-24 md:pb-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <SectionHeader
              eyebrow={howItWorks.eyebrow}
              title={howItWorks.title}
              description={howItWorks.description}
              variant="light"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {howItWorks.features.map((f, i) => (
                <FeatureCard key={i} icon={f.icon} title={f.title} description={f.description} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === PRODUCT CAROUSEL === */}
      {productCarousel && productCarousel.products.length > 0 && (
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6">
          <HorizontalCarousel
            eyebrow={productCarousel.eyebrow}
            title={productCarousel.title}
            description={productCarousel.description}
            bgClass="bg-white"
          >
            {productCarousel.products.map((p, i) => (
              <ProductCarouselCard key={i} product={p} />
            ))}
          </HorizontalCarousel>
        </div>
      )}

      {/* === CONSIDERATIONS === */}
      {considerations && considerations.items.length > 0 && (
        <section className="bg-gray-50 py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <PageSectionHeader eyebrow={considerations.eyebrow} title={considerations.title} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {considerations.items.map((c) => (
                <ConsiderationCard
                  key={c.title}
                  icon={c.icon}
                  title={c.title}
                  description={c.description}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === RELATED TOOLS === */}
      {relatedTools && relatedTools.length > 0 && (
        <section className="bg-white py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <PageSectionHeader eyebrow="Pair It Up" title="Tools That Work With This One" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {relatedTools.map((t) => (
                <ToolLinkCard
                  key={t.href}
                  title={t.title}
                  description={t.description}
                  href={t.href}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === FAQ === */}
      {faqs.length > 0 && (
        <section className="bg-gray-50 py-14">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <PageSectionHeader eyebrow="Frequently Asked" title="Questions & Answers" />
            <FAQAccordion items={faqs} columns={2} defaultOpen={[0, 1]} bordered={false} />
          </div>
        </section>
      )}

      {/* === KEEP EXPLORING === */}
      {exploreLinks.length > 0 && (
        <section className="bg-white py-14 border-t border-gray-100">
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <PageSectionHeader eyebrow="Keep Exploring" title={exploreTitle} />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {exploreLinks.map((c) => (
                <CategoryCard key={c.label} label={c.label} href={c.href} icon={c.icon} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* === CTA === */}
      <section className="bg-white pt-14 pb-24 md:pt-16 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8">
          <CTABanner
            title={cta.title}
            description={cta.description}
            buttonText={cta.buttonText}
            buttonHref={cta.buttonHref}
          />
        </div>
      </section>

      <SiteFooter />
    </>
  )
}

export type {
  HeroStat,
  HowItWorksFeature,
  ConsiderationItem,
  RelatedTool,
  ExploreLink,
  FAQItem,
  SidebarInfoCard,
}
